'use client'

import { useState, useCallback, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FormEditPanelWrapper } from '@/components/forms/form-edit-panel-wrapper'
import { useProjectStore } from '@/hooks/use-project-store'
import { updateFormConfiguration } from '@/lib/editor/form-config'
import { useSaveChanges } from '@/lib/editor/hooks'
import { Button } from '@/components/ui/button'
import { popModal } from '@/components/modals'
import { LoaderCircle } from 'lucide-react'
import { useConfirm } from '@/hooks/use-confirm'

interface FormEditModalProps {
  schemaId: string
  onSuccess?: () => void
  onClose?: () => void
}

export function FormEditModal({
  schemaId,
  onSuccess,
  onClose,
}: FormEditModalProps) {
  const t = useTranslations('forms')
  const { project, aiStage, setProject } = useProjectStore()
  const { save, loading: saving, setError } = useSaveChanges()
  const { isOpen: confirmDialogOpen } = useConfirm()

  const [hasEmailSettingsChanged, setHasEmailSettingsChanged] = useState(false)
  const [hasFormFieldsChanged, setHasFormFieldsChanged] = useState(false)
  const [formConfig, setFormConfig] = useState<any>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [mountKey, setMountKey] = useState(Date.now()) // Force fresh mount of child components

  // Reset state on mount
  useEffect(() => {
    // Reset all state to initial values when modal opens
    setHasEmailSettingsChanged(false)
    setHasFormFieldsChanged(false)
    setFormConfig(null)
    setIsSaving(false)
    setMountKey(Date.now()) // Force child components to remount with fresh state
  }, [])

  const handleEmailSettingsChange = (changed: boolean) => {
    setHasEmailSettingsChanged(changed)
  }

  const handleFormConfigChange = (config: any) => {
    setFormConfig(config)
  }

  const handleFormFieldsChange = (changed: boolean) => {
    setHasFormFieldsChanged(changed)
  }

  const handleSave = useCallback(async () => {
    if (!project?._id || !project?.file_system_tree) return

    const hasChanges = hasEmailSettingsChanged || hasFormFieldsChanged
    if (!hasChanges || !formConfig) {
      handleClose()
      return
    }

    setIsSaving(true)
    try {
      // Update the form configuration in the file system tree
      const updatedTree = updateFormConfiguration(
        project.file_system_tree,
        schemaId,
        formConfig,
      )

      // Save to the remote database using the same pattern as edit-panel
      const data = await save(project._id.toString(), updatedTree)

      if (data) {
        const nextIndex =
          data.file_system_tree_index ?? project.file_system_tree_index
        const nextFileSystemTreeList = [
          ...(project.file_system_tree_list || []),
        ]
        nextFileSystemTreeList[nextIndex] = updatedTree

        // Update the local project state with the new data
        setProject({
          ...project,
          ...data,
          file_system_tree: updatedTree,
          file_system_tree_list: nextFileSystemTreeList,
        })

        // Call onSuccess to refresh the forms list
        if (onSuccess) {
          onSuccess()
        }

        handleClose()
      }
    } catch (error) {
      console.error('Error saving form configuration:', error)
      setError(error as Error)
    } finally {
      setIsSaving(false)
    }
  }, [
    project,
    hasEmailSettingsChanged,
    hasFormFieldsChanged,
    formConfig,
    schemaId,
    save,
    setProject,
    onSuccess,
    setError,
  ])

  const handleClose = () => {
    popModal()

    if (onClose) {
      onClose()
    }
  }

  const hasChanges = hasEmailSettingsChanged || hasFormFieldsChanged
  const isLoading = isSaving || saving

  return (
    <DialogContent
      className="max-w-3xl max-h-[90vh] overflow-y-auto"
      noCloseButton={false}
      onPointerDownOutside={(e) => {
        // Check if we're interacting with any alert dialog
        const target = e.target as HTMLElement
        const isAlertDialog =
          confirmDialogOpen ||
          target.closest('[role="alertdialog"]') !== null ||
          target.closest('.sonner-toast') !== null ||
          document.querySelector('[role="alertdialog"]') !== null

        if (isAlertDialog) {
          e.preventDefault()
        }
      }}
      onEscapeKeyDown={(e) => {
        // Prevent closing if confirm dialog is open
        if (confirmDialogOpen) {
          e.preventDefault()
        }
      }}
      onInteractOutside={(e) => {
        // Check if we're interacting with any alert dialog
        const target = e.target as HTMLElement
        const isAlertDialog =
          confirmDialogOpen ||
          target.closest('[role="alertdialog"]') !== null ||
          target.closest('.sonner-toast') !== null ||
          document.querySelector('[role="alertdialog"]') !== null

        if (isAlertDialog) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
    >
      <DialogHeader>
        <DialogTitle className="text-white text-lg">
          {t('edit_form_title', { schemaId })}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-6 relative">
        {isLoading && (
          <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center rounded">
            <LoaderCircle className="w-8 h-8 animate-spin text-white" />
          </div>
        )}

        <FormEditPanelWrapper
          key={mountKey} // Force remount with fresh state
          schemaId={schemaId}
          onEmailSettingsChange={handleEmailSettingsChange}
          onFormConfigChange={handleFormConfigChange}
          onFormFieldsChange={handleFormFieldsChange}
        />

        {/* Action Buttons */}
        <div className="pt-4 border-t border-white/10 space-y-2">
          <div className="flex justify-end gap-2">
            <Button
              variant="transparent"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="active"
              onClick={handleSave}
              disabled={!hasChanges || isLoading || aiStage === 'coding'}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <LoaderCircle className="w-4 h-4 animate-spin" />
                  {t('saving')}
                </div>
              ) : (
                t('save_changes')
              )}
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  )
}
